import API from 'shared/api/API';
import { QUALITY_METRICS_SERVICE_URL as servicePath } from 'shared/constants/APIconstants';
import { DELETE, GET, POST, PUT } from 'shared/utils/Executer';
import type {
  QualityMetricOrganizationConfiguration,
  QualityMetricRun,
} from 'shared/api/quality-metric-rq.service.api';

/**
 * What every `Executer` call resolves to. It never throws: a failure comes back
 * with `error` set, `data` holding the error body rather than the success shape,
 * and `code` carrying the server's machine-readable code (or `-1` when there was
 * no response at all).
 */
export interface ExecuterResult<T> {
  data: T;
  code?: unknown;
  error?: unknown;
  success?: boolean;
}

/**
 * An organization's quality-metric ingestion configuration: which parser the
 * sync service runs on its exports and, per source, which site it pulls from.
 *
 * It is its own document (`quality_metric_organization_configurations`), not a
 * block on the organization, so it is read and written here rather than through
 * `PATCH /v1/organizations/:id`. The organization is the session's, so the
 * caller must have switched into it first — the organization page does that
 * on open (`organization.context.jsx`).
 */
const CONFIGURATION_PATH = `${servicePath}configuration`;

/**
 * Reads the session organization's ingestion configuration. The credential
 * value is never returned, only `mpog.secretName`.
 *
 * @returns The envelope. An organization nobody has configured has no document
 *   at all and answers `404` with `code:
 *   QUALITY_METRIC_ERROR_CODES.CONFIGURATION_NOT_FOUND` — an empty form, not a
 *   failure, so callers branch on that code rather than on `error` alone.
 * @example
 * const res = await getQualityMetricConfiguration();
 * if (res.error && res.code !== QUALITY_METRIC_ERROR_CODES.CONFIGURATION_NOT_FOUND) fail(res);
 */
export const getQualityMetricConfiguration = async (): Promise<
  ExecuterResult<QualityMetricOrganizationConfiguration>
> => {
  return API.client.execute(GET, CONFIGURATION_PATH);
};

/**
 * Upserts the configuration, merged field by field.
 *
 * @param args.data - Only the block being changed. `mapper.default` and
 *   `mpog.configurationName` each take `null` to clear; `secretName` and
 *   `mpog.runs` in the body are ignored, as they move only through
 *   {@link setQualityMetricCredentials} and the runs endpoints.
 * @returns The envelope; `data` is the updated document, so there is nothing
 *   to re-read.
 * @example
 * const { data, error } = await setQualityMetricConfiguration({
 *   data: { mapper: { default: 'mpog' } },
 * });
 */
export const setQualityMetricConfiguration = async ({
  data,
}: {
  data: Partial<QualityMetricOrganizationConfiguration>;
}): Promise<ExecuterResult<QualityMetricOrganizationConfiguration>> => {
  return API.client.execute(PUT, CONFIGURATION_PATH, data);
};

/**
 * Stores or rotates the credentials for a pull source. The values go to Secret
 * Manager and only the secret *name* is recorded, so nothing here is ever
 * returned — never log, echo or re-display the body. Calling it again rotates
 * them as a new secret version.
 *
 * `printErrorLogs: false` is load-bearing, not tidiness: on failure the
 * Executer logs `{ url, action, data }` through `console.warn`, and `data` is
 * this request body — so the default would write the password into the
 * administrator's browser console, including after the 5xx retries run out.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @param args.username - 1–512 chars.
 * @param args.password - 1–4096 chars.
 * @returns The envelope with no `data`: the API answers `204`, so the caller
 *   re-reads the configuration to pick up `mpog.secretName`. A `500` means
 *   Secret Manager was unavailable and nothing was written; retry the call.
 * @example
 * const { error } = await setQualityMetricCredentials({
 *   source: SOURCE, username, password,
 * });
 */
export const setQualityMetricCredentials = async ({
  source,
  username,
  password,
}: {
  source: string;
  username: string;
  password: string;
}): Promise<ExecuterResult<undefined>> => {
  return API.client.execute(
    PUT,
    `${CONFIGURATION_PATH}/${source}/credentials`,
    { username, password },
    { printErrorLogs: false },
  );
};

/**
 * A run as every runs endpoint returns it: what is stored, plus the derived
 * `window` (`"3mo"` or `"10-01-2025"`, unique per organization and source),
 * `nextRunAt` (the next tick in the API's time zone, `null` while disabled) and
 * the live `scheduler` state, which is `null` when the job does not exist —
 * that absence is what the table shows as "not synced".
 */
export interface QualityMetricRunView extends QualityMetricRun {
  window: string;
  nextRunAt: string | null;
  scheduler: {
    name: string;
    state: 'ENABLED' | 'PAUSED' | string | null;
    scheduleTime: string | null;
    lastAttemptTime: string | null;
    lastAttemptStatus: string | null;
  } | null;
}

/**
 * What the API did in Cloud Scheduler for this organization. A Scheduler
 * failure never fails the write: the run is saved, the failure is listed in
 * `errors`, and the next write or a re-sync repairs it — so a write can
 * succeed and still need the re-sync banner. `managed: false` means this
 * environment owns no Scheduler jobs and nothing was sent at all.
 */
export interface QualityMetricReconcile {
  managed: boolean;
  organizations: number;
  created: number;
  updated: number;
  paused: number;
  resumed: number;
  deleted: number;
  unchanged: number;
  errors: Array<{ name: string | null; action: string; message: string }>;
}

/** The writable fields of a run. `PUT` is a full replace, so send all of them. */
export interface QualityMetricRunPayload {
  schedule: string;
  lookback?: string;
  startDate?: string;
  enabled?: boolean;
}

const runsPath = (source: string) => `${CONFIGURATION_PATH}/${source}/runs`;

/**
 * Reads the organization's scheduled pulls for one source, with the live
 * Scheduler state behind each one.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @returns The envelope. On success `data.runs` holds the runs, `data.timeZone`
 *   is the zone the schedules are evaluated in, and `data.managed` is `false`
 *   where no Scheduler jobs are owned — stored but never ticking, which the UI
 *   has to say rather than show as healthy.
 * @example
 * const { data, error } = await getQualityMetricRuns({ source: SOURCE });
 */
export const getQualityMetricRuns = async ({
  source,
}: {
  source: string;
}): Promise<
  ExecuterResult<{ managed: boolean; timeZone: string; runs: QualityMetricRunView[] }>
> => {
  return API.client.execute(GET, runsPath(source));
};

/**
 * Adds a scheduled pull and mirrors it as a Cloud Scheduler job.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @param args.data - The run: schedule, enabled flag and exactly one window field.
 * @returns The envelope; `data.run` is the stored run and `data.reconcile` what
 *   Scheduler did. `code` is `RUN_WINDOW_TAKEN` when another run of this
 *   organization already covers the same window — two runs pulling the same
 *   data twice is the thing being prevented.
 * @example
 * const { data, error, code } = await createQualityMetricRun({
 *   source: SOURCE, data: { schedule: '0 0 * * 4', lookback: '3mo' },
 * });
 */
export const createQualityMetricRun = async ({
  source,
  data,
}: {
  source: string;
  data: QualityMetricRunPayload;
}): Promise<ExecuterResult<{ run: QualityMetricRunView; reconcile: QualityMetricReconcile }>> => {
  return API.client.execute(POST, runsPath(source), data);
};

/**
 * Replaces a run's fields, keeping its `_id`. Unlike the configuration this is
 * a full replace, so every field has to be sent: an omitted `lookback` does not
 * keep the stored one. The job is named by the run id, so changing the window
 * updates that same job rather than replacing it.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @param args.runId - The run's `_id`.
 * @param args.data - The complete run, not a patch.
 * @returns The envelope; `data.run` is the stored run and `data.reconcile` what
 *   Scheduler did. `code` is `RUN_NOT_FOUND` or `RUN_WINDOW_TAKEN` on conflict.
 * @example
 * const { error } = await updateQualityMetricRun({
 *   source: SOURCE, runId: run._id, data: toPayload(run, false),
 * });
 */
export const updateQualityMetricRun = async ({
  source,
  runId,
  data,
}: {
  source: string;
  runId: string;
  data: QualityMetricRunPayload;
}): Promise<ExecuterResult<{ run: QualityMetricRunView; reconcile: QualityMetricReconcile }>> => {
  return API.client.execute(PUT, `${runsPath(source)}/${runId}`, data);
};

/**
 * Deletes a run and the Cloud Scheduler job mirroring it. Nothing pulls on that
 * schedule again, and the window it held becomes free for another run.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @param args.runId - The run's `_id`.
 * @returns The envelope; `data.reconcile` is what Scheduler did. `code` is
 *   `RUN_NOT_FOUND` when the run is already gone.
 * @example
 * const { error } = await deleteQualityMetricRun({ source: SOURCE, runId });
 */
export const deleteQualityMetricRun = async ({
  source,
  runId,
}: {
  source: string;
  runId: string;
}): Promise<ExecuterResult<{ reconcile: QualityMetricReconcile }>> => {
  return API.client.execute(DELETE, `${runsPath(source)}/${runId}`);
};

/**
 * Ticks a run's job now, outside its schedule. The `202` only asks for the
 * pull; it does not wait for the records to land.
 *
 * @param args.source - The pull source; `mpog` is the only one today.
 * @param args.runId - The run's `_id`.
 * @returns The envelope. `code` is `SCHEDULER_NOT_MANAGED` where this
 *   environment runs no schedulers, or `SCHEDULER_NOT_FOUND` when the job is
 *   missing and {@link reconcileQualityMetricSchedulers} has to come first.
 * @example
 * const { error, code } = await triggerQualityMetricRun({
 *   source: SOURCE, runId: run._id,
 * });
 */
export const triggerQualityMetricRun = async ({
  source,
  runId,
}: {
  source: string;
  runId: string;
}): Promise<ExecuterResult<{ name: string; requestedAt: string }>> => {
  return API.client.execute(POST, `${runsPath(source)}/${runId}/trigger`);
};

/**
 * Re-creates whatever is missing in Cloud Scheduler, from the runs that are
 * stored. This is the repair for a write whose `reconcile` came back with
 * `errors`, and for a run whose `scheduler` reads `null`.
 *
 * @param args.source - Limits the re-sync to one source; omit for all of them.
 * @returns The envelope; `data` is the reconcile summary.
 * @example
 * const { data, error } = await reconcileQualityMetricSchedulers({ source: SOURCE });
 */
export const reconcileQualityMetricSchedulers = async ({
  source,
}: {
  source?: string;
}): Promise<ExecuterResult<QualityMetricReconcile>> => {
  return API.client.execute(
    POST,
    `${servicePath}schedulers/reconciliations`,
    source ? { source } : {},
  );
};
